import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Output,
  EventEmitter,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, map } from 'rxjs';
import { LucideAngularModule, CheckCircle2, Info, Plus, ChevronDown, ChevronUp, Check, Trash2 } from 'lucide-angular';
import { LessonPlanService } from '../../services/lesson-plan.service';
import { THEME_COLOR, LessonObjective } from '../../services/lesson-plan.models';
import { ConfirmModalComponent } from '../../components/confirm-modal/confirm-modal';
import { StepStructureLoadingComponent } from '../step-structure-loading/step-structure-loading';
import { AutoResizeTextareaDirective } from '../../../../../common/directives/auto-resize-textarea.directive';

@Component({
  selector: 'app-step-objectives',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, ConfirmModalComponent, StepStructureLoadingComponent, AutoResizeTextareaDirective],
  templateUrl: './step-objectives.html',
  styleUrl: './step-objectives.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepObjectivesComponent {
  private readonly lessonPlanService = inject(LessonPlanService);
  private readonly destroyRef = inject(DestroyRef);

  @Output() readonly back = new EventEmitter<void>();
  @Output() readonly next = new EventEmitter<void>();
  @ViewChild(StepStructureLoadingComponent) structureLoadingComponent?: StepStructureLoadingComponent;

  protected readonly themeColor = THEME_COLOR;
  protected readonly inputData$ = this.lessonPlanService.inputData$;
  protected readonly error$ = this.lessonPlanService.error$;

  /** Whether we're showing the loading screen for generating structure */
  private readonly showStructureLoading$$ = new BehaviorSubject<boolean>(false);
  protected readonly showStructureLoading$ = this.showStructureLoading$$.asObservable();

  /** Sorted objectives: AI objectives by relevance (desc), then manual objectives by createdAt (asc) */
  protected readonly objectives$ = this.lessonPlanService.objectives$.pipe(
    map((objectives) => this.sortObjectives(objectives))
  );

  protected customText = '';

  /** Track which objectives have their details expanded */
  protected readonly expandedDetails = new Set<string>();

  protected readonly icons = {
    CheckCircle2,
    Info,
    Plus,
    ChevronDown,
    ChevronUp,
    Check,
    Trash2,
  };

  // Delete confirmation modal state
  protected isDeleteModalOpen = false;
  protected objectiveToDelete: LessonObjective | null = null;

  protected toggleObjective(id: string): void {
    this.lessonPlanService.toggleObjective(id);
  }

  protected addCustomObjective(textarea?: HTMLTextAreaElement): void {
    if (!this.customText.trim()) return;
    this.lessonPlanService.addCustomObjective(this.customText.trim());
    this.customText = '';
    if (textarea) {
      textarea.style.height = 'auto';
    }
  }

  protected openDeleteModal(event: Event, objective: LessonObjective): void {
    event.stopPropagation();
    this.objectiveToDelete = objective;
    this.isDeleteModalOpen = true;
  }

  protected confirmDelete(): void {
    if (this.objectiveToDelete) {
      this.lessonPlanService.deleteObjective(this.objectiveToDelete.id);
    }
    this.closeDeleteModal();
  }

  protected closeDeleteModal(): void {
    this.isDeleteModalOpen = false;
    this.objectiveToDelete = null;
  }

  protected onBack(): void {
    this.back.emit();
  }

  protected onContinue(): void {
    // Show loading screen
    this.showStructureLoading$$.next(true);

    // Confirm objectives and generate structure
    this.lessonPlanService
      .confirmObjectivesAndGenerateStructure()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          // Complete the loading animation
          this.structureLoadingComponent?.complete();

          // Delay to show 100% progress and all steps completed before transitioning
          // 1000ms gives users time to see the completion state
          setTimeout(() => {
            this.next.emit();
          }, 1000);
        },
        error: () => {
          // Error is already handled by the service
          this.showStructureLoading$$.next(false);
        },
      });
  }

  protected getSelectedCount(objectives: LessonObjective[]): number {
    return objectives.filter((o) => o.selected).length;
  }

  /**
   * Get relevance as percentage string with label (e.g., "100% Relevance")
   */
  protected getRelevancePercent(relevance: number): string {
    return `${Math.round((relevance / 10) * 100)}% Relevance`;
  }

  /**
   * Format grade number as display string (e.g., 0 -> "K", 1 -> "1st Grade")
   */
  protected formatGrade(grade: number): string {
    if (grade === 0) return 'K';
    return `${this.getOrdinalSuffix(grade)} Grade`;
  }

  /**
   * Get ordinal suffix for a number (1st, 2nd, 3rd, 4th, etc.)
   */
  private getOrdinalSuffix(n: number): string {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }

  /**
   * Toggle details visibility for an objective
   */
  protected toggleDetails(event: Event, objectiveId: string): void {
    event.stopPropagation(); // Prevent triggering the parent click (toggle selection)
    if (this.expandedDetails.has(objectiveId)) {
      this.expandedDetails.delete(objectiveId);
    } else {
      this.expandedDetails.add(objectiveId);
    }
  }

  /**
   * Check if details are expanded for an objective
   */
  protected isDetailsExpanded(objectiveId: string): boolean {
    return this.expandedDetails.has(objectiveId);
  }

  /**
   * Check if a grade level is above the selected grade (not compliant yet)
   */
  protected isGradeAboveSelected(grade: number, selectedGrade: number): boolean {
    return grade > selectedGrade;
  }

  /**
   * Check if a grade level is below the selected grade
   */
  protected isGradeBelowSelected(grade: number, selectedGrade: number): boolean {
    return grade < selectedGrade;
  }

  /**
   * Get the list of grades to display for an objective.
   * Combines objective grades with the current selected grade (if not already present),
   * then sorts them in ascending order.
   */
  protected getDisplayGrades(objectiveGrades: number[], currentGrade: number): number[] {
    const grades = new Set(objectiveGrades);
    grades.add(currentGrade);
    return Array.from(grades).sort((a, b) => a - b);
  }

  /**
   * Sort objectives: AI objectives by relevance (desc), manual objectives at bottom sorted by createdAt (asc)
   */
  private sortObjectives(objectives: LessonObjective[]): LessonObjective[] {
    const aiObjectives = objectives.filter((o) => o.type === 'ai');
    const manualObjectives = objectives.filter((o) => o.type === 'manual');

    // Sort AI objectives by relevance descending
    aiObjectives.sort((a, b) => (b.relevance ?? 0) - (a.relevance ?? 0));

    // Sort manual objectives by createdAt ascending
    manualObjectives.sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0));

    return [...aiObjectives, ...manualObjectives];
  }

}
